<template>
  <section class="eva-page">
    <v-row>
      <v-col cols="12" offset="0" md="10" offset-md="1">
        <div class="eva-top">
          <v-breadcrumbs
            large
            :items="crumbs"
            divider="/"
            class="pl-0 eva-breadcrumbs"
          />

          <div class="eva-titlebar">
            <div class="eva-title">
              <v-icon color="primary" class="mr-2">mdi-clipboard-text-clock</v-icon>
              <span class="primary--text">Listagem de ordens de trabalho</span>
            </div>
          </div>
        </div>

        <v-card class="eva-card mt-6" elevation="10">
          <v-card-title class="eva-card__title primary white--text">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="white">mdi-clipboard-list-outline</v-icon>
              <span class="font-weight-bold">Ordens de Trabalho</span>
            </div>

            <v-spacer />

            <v-chip color="white" outlined class="eva-chip">
              <v-icon left small>mdi-format-list-bulleted</v-icon>
              {{ total }} registos
            </v-chip>
          </v-card-title>

          <v-card-text class="pt-6">
            <div style="position: relative">
              <v-data-table
                :headers="headers"
                :items="items"
                :server-items-length="total"
                :loading="loading"
                :footer-props="tableFooter"
                :options.sync="options"
                class="eva-table"
                locale="pt-pt"
              >
                <template v-slot:top>
                  <div class="d-flex align-center justify-space-between px-4 py-2">
                    <div class="text-subtitle-1 font-weight-bold">
                      Ordens de Trabalho
                    </div>

                    <v-btn
                      class="primary"
                      small
                      @click="$router.push('ordens-trabalho/create')"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Adicionar
                    </v-btn>
                  </div>
                </template>

                <template v-slot:item.name="{ item }">
                  <span class="font-weight-bold">
                    {{ item.name || "—" }}
                  </span>
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip small :color="getStatusColor(item.status)" dark>
                    {{ getStatusLabel(item.status) }}
                  </v-chip>
                </template>

                <template v-slot:item.obra_label="{ item }">
                  <div class="cell-main">
                    {{ getObraText(item) }}
                  </div>
                </template>

                <template v-slot:item.frente_label="{ item }">
                  <div class="cell-main">
                    {{ getFrenteText(item) }}
                  </div>
                </template>

                <template v-slot:item.classe_label="{ item }">
                  <div class="cell-main">
                    {{ getClasseText(item) }}
                  </div>
                </template>

                <template v-slot:item.total_cost="{ item }">
                  <span class="font-weight-medium">
                    {{ formatCurrency(item.total_cost) }}
                  </span>
                </template>

                <template v-slot:item.created_by="{ item }">
                  {{ item.creator ? item.creator.name : "—" }}
                </template>

                <template v-slot:item.created_at="{ item }">
                  {{ getTableDate(item.created_at) }}
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-row align="center" justify="center" no-gutters>
                    <v-col cols="auto">
                      <v-icon
                        small
                        color="primary"
                        class="mr-3"
                        @click="$router.push('ordens-trabalho/' + item.id)"
                      >
                        mdi-eye
                      </v-icon>

                      <IconRemove
                        class="mr-2"
                        @on-submit="deleteOrdemTrabalho(item)"
                      />
                    </v-col>
                  </v-row>
                </template>

                <template v-slot:no-data>
                  <div class="pa-6 grey--text text--darken-1">
                    Sem resultados.
                  </div>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import OrdensTrabalho from "@/api/OrdensTrabalho.js";
import dayjs from "dayjs";
import IconRemove from "@/components/UI/IconRemove.vue";

export default {
  name: "OrdensTrabalho",

  components: {
    IconRemove,
  },

  watch: {
    options: {
      handler() {
        setTimeout(() => {
          this.searchOrdensTrabalho();
        }, 10);
      },
      deep: true,
    },
  },

  beforeCreate() {
    document.title = "ODC - Listagem de ordens de trabalho";
  },

  data: () => ({
    headers: [
      { text: "Nome", sortable: false, value: "name", width: "120px" },
      { text: "Estado", sortable: false, value: "status", width: "150px" },
      { text: "Obra", sortable: false, value: "obra_label", width: "260px" },
      { text: "Frente", sortable: false, value: "frente_label", width: "220px" },
      { text: "Classe", sortable: false, value: "classe_label", width: "220px" },
      { text: "Custo estimado", sortable: false, value: "total_cost", width: "140px" },
      { text: "Criado por", sortable: false, value: "created_by", width: "140px" },
      { text: "Criado em", sortable: false, value: "created_at", width: "120px" },
      { text: "Opções", align: "center", sortable: false, value: "actions", width: "100px" },
    ],

    items: [],
    total: 0,
    loading: false,
    searching: false,

    tableFooter: {
      "items-per-page-options": [5, 10, 15, 20],
      "items-per-page-text": "Linhas por página",
    },

    options: {},
    filter: {},
  }),

  computed: {
    crumbs() {
      return [
        { text: "Dashboard", disabled: false, to: "/admin", exact: true },
        {
          text: "Listagem de ordens de trabalho",
          disabled: true,
          to: window.location.href,
          exact: true,
        },
      ];
    },
  },

  mounted() {
    
    if (!this.$store.getters.hasPermission(["super", "work_orders.read"])) {
      this.$router.push("/no-permission");
      return;
    }
    
    this.fillBaseData();
  },

  methods: {
    fillBaseData() {
      const key = "ordens-trabalho-list-" + this.$store.state.user.data.id;

      if (localStorage[key] !== undefined) {
        const data = JSON.parse(localStorage[key]);

        this.filter = data.filter || {};
        this.options = data.options || {};
      }
    },

    searchOrdensTrabalho(event) {
      if (!!event && event.type === "submit") {
        this.options.page = 1;
      }

      this.loading = true;

      const request = {
        page: this.options.page,
        limit: this.options.itemsPerPage,
      };

      Object.assign(request, { ...this.filter });

      localStorage[
        "ordens-trabalho-list-" + this.$store.state.user.data.id
      ] = JSON.stringify({
        filter: this.filter,
        options: this.options,
      });

      OrdensTrabalho.search(request)
        .then((response) => {
          const data = response.data;

          this.total = data.total || 0;
          this.items = data.data || [];
          this.searching = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    resetOrdensTrabalho() {
      this.filter = {};
      this.searchOrdensTrabalho();
    },

    deleteOrdemTrabalho(item) {
      this.loading = true;

      OrdensTrabalho.delete(item.id)
        .then(() => {
          this.searchOrdensTrabalho();
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getStatusLabel(status) {
      const labels = {
        parada: "Parada",
        concluida: "Concluída",
        em_andamento: "Em andamento",
      };

      return labels[status] || status || "—";
    },

    getStatusColor(status) {
      const colors = {
        parada: "grey",
        concluida: "success",
        em_andamento: "info",
      };

      return colors[status] || "grey";
    },

    getObraText(item) {
      if (item.obra_label) {
        return item.obra_label;
      }

      if (item.obra_info && item.obra_info.label) {
        return item.obra_info.label;
      }

      if (item.obra) {
        const codigo = item.obra.Codigo || item.obra.codigo || "";
        const descricao =
          item.obra.Descricao ||
          item.obra.descricao ||
          item.obra.Nome ||
          item.obra.name ||
          "";

        return this.joinCodeAndDescription(codigo, descricao);
      }

      return "—";
    },

    getFrenteText(item) {
      if (item.frente_label) {
        return this.removeDuplicatedCodeFromLabel(item.frente_label);
      }

      if (item.frente_info && item.frente_info.label) {
        return this.removeDuplicatedCodeFromLabel(item.frente_info.label);
      }

      if (item.frente) {
        const subEmp = item.frente.SubEmp || item.frente.subemp || "";
        const descricao =
          item.frente.Descricao ||
          item.frente.descricao ||
          item.frente.name ||
          "";

        return this.joinCodeAndDescriptionWithoutDuplicate(subEmp, descricao);
      }

      return "—";
    },

    getClasseText(item) {
      if (item.classe_label) {
        return this.removeDuplicatedCodeFromLabel(item.classe_label);
      }

      if (item.classe_info && item.classe_info.label) {
        return this.removeDuplicatedCodeFromLabel(item.classe_info.label);
      }

      if (item.classe) {
        const classe = item.classe.Classe || item.classe.classe || "";
        const descricao =
          item.classe.Descricao ||
          item.classe.descricao ||
          item.classe.name ||
          "";

        return this.joinCodeAndDescriptionWithoutDuplicate(classe, descricao);
      }

      return "—";
    },

    joinCodeAndDescriptionWithoutDuplicate(code, description) {
      const cleanCode = code ? String(code).trim() : "";
      const cleanDescription = description ? String(description).trim() : "";

      if (!cleanCode && !cleanDescription) {
        return "—";
      }

      if (!cleanCode) {
        return cleanDescription;
      }

      if (!cleanDescription) {
        return cleanCode;
      }

      const normalizedDescription = cleanDescription.toLowerCase();
      const normalizedCode = cleanCode.toLowerCase();

      if (
        normalizedDescription === normalizedCode ||
        normalizedDescription.startsWith(normalizedCode + " -") ||
        normalizedDescription.startsWith(normalizedCode + "-")
      ) {
        return cleanDescription;
      }

      return `${cleanCode} - ${cleanDescription}`;
    },

    removeDuplicatedCodeFromLabel(label) {
      if (!label) {
        return "—";
      }

      const cleanLabel = String(label).trim();

      const parts = cleanLabel.split(" - ");

      if (parts.length < 3) {
        return cleanLabel;
      }

      const first = parts[0].trim().toLowerCase();
      const second = parts[1].trim().toLowerCase();

      if (first === second) {
        return parts.slice(1).join(" - ");
      }

      return cleanLabel;
    },

    formatCurrency(value) {
      const number = Number(value || 0);

      return number.toLocaleString("pt-PT", {
        style: "currency",
        currency: "EUR",
      });
    },

    getTableDate(date) {
      return date != null ? dayjs(date).toDate().toLocaleDateString("pt-PT") : "—";
    },
  },
};
</script>

<style scoped>
.eva-page {
  padding-bottom: 32px;
}

.eva-top {
  margin-top: 8px;
}

.eva-breadcrumbs :deep(.v-breadcrumbs__item) {
  font-size: 13px;
}

.eva-titlebar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
}

.eva-title {
  display: flex;
  align-items: center;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.2px;
}

.eva-card {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.eva-card__title {
  font-weight: 800;
  letter-spacing: 0.2px;
}

.eva-chip {
  border-radius: 999px;
}

.eva-table {
  border-radius: 14px;
  overflow: hidden;
}

.eva-table :deep(thead th) {
  font-weight: 800;
  color: rgba(0, 0, 0, 0.75);
  letter-spacing: 0.2px;
  white-space: nowrap;
}

.eva-table :deep(tbody tr:hover) {
  background: rgba(0, 0, 0, 0.02);
}

.cell-main {
  max-width: 260px;
  white-space: normal;
  line-height: 1.35;
  word-break: break-word;
}
</style>